// Raw Material Map — frontend mirror of backend RAW_MATERIAL_DEPENDENCIES
// (see /app/backend/work_units.py). Plus user-confirmed Sound sub-type rules:
//
//    Sound · Voice Over (VO)  → Script + Word Count
//    Sound · BGM              → Animatics (planning) + Animation (final pass)
//    Sound · Foley            → Animatics (planning) + Animation (final pass)
//
// One source of truth so VendorTasksPage and the Live View dashboard render
// the SAME raw material per task. Tight by design — no over-cascading.

// Each value is a list of bucket KEYS in the /api/me/references payload
// that should be surfaced for this task_type.
//   scripts          → Project Script + Synopsis + Logline (text from Brief Editor)
//   reference_links  → YouTube / Moodboard / Vimeo links from Brief Editor
//   concept_art      → Approved Concept Art submissions
//   vo               → Approved Voice Over submissions
//   animatics        → Approved Animatics submissions
//   asset_build      → Approved Asset Build submissions
//   rigging          → Approved Rigging submissions
//   shading          → Approved Shading submissions
//   layout           → Approved Layout submissions
//   animation        → Approved Animation submissions
//   lighting         → Approved Lighting submissions
//   fx_comp          → Approved FX/Comp submissions
const RAW_MATERIAL_BUCKETS = {
  // ── sequence-mode ────────────────────────────────────────────────
  script:     [],                                            // start of chain
  storyboard: ['scripts'],                                   // script→storyboard
  animatics:  ['scripts', 'concept_art', 'vo', 'reference_links'],
  cba:        ['animatics'],
  music:      ['animatics'],
  // sound is handled via soundBucketsFor() — see below

  // ── asset-mode ───────────────────────────────────────────────────
  concept_art: [],                                           // start of chain
  asset_build: ['concept_art'],
  modeling:    ['concept_art'],                              // legacy
  texturing:   ['asset_build'],                              // legacy
  shading:     ['asset_build'],
  rigging:     ['asset_build'],
  lookdev:     ['shading'],

  // ── shot-mode ────────────────────────────────────────────────────
  layout:      ['animatics', 'rigging', 'vo'],
  blocking:    ['layout'],
  animation:   ['layout', 'animatics'],
  anim:        ['layout', 'animatics'],
  lighting:    ['animation'],
  light:       ['animation'],
  fx:          ['animation'],
  comp:        ['lighting', 'fx_comp'],
  compositing: ['lighting', 'fx_comp'],
  render:      ['comp'],
  rendering:   ['comp'],
};

// Sound has 3 sub-types (set on the WO / task as `sound_subtype` /
// `sound_type` / `scope.sound_type`). Per user-confirmed table:
function soundBucketsFor(task) {
  const sub = String(
    task?.sound_subtype || task?.sound_type ||
    task?.scope?.sound_type || task?.scope?.sound_subtype ||
    ''
  ).toLowerCase().replace(/[^a-z]/g, '');
  if (['vo', 'voice', 'voiceover', 'voiceovers'].includes(sub)) {
    return ['scripts'];                                       // VO needs script + word count
  }
  if (['bgm', 'foley'].includes(sub)) {
    return ['animatics', 'animation'];                        // both need anim+animation
  }
  // Unknown / generic sound — show all 3 buckets so vendor can self-pick
  return ['scripts', 'animatics', 'animation'];
}

// Public API
export function bucketsForTask(task) {
  if (!task) return [];
  const tt = String(task.task_type || '').toLowerCase().trim();
  if (tt === 'sound' || tt === 'sound_design') return soundBucketsFor(task);
  return RAW_MATERIAL_BUCKETS[tt] || [];
}

// Resolves the actual filtered items for a task from a `/me/references` payload.
// Returns an ordered array of { bucket, items } so the UI can render labelled
// sections in dependency order.
//
// SCOPING RULES:
//  • `scripts`, `reference_links` — project-grouped, filtered to the task's
//    project_id.
//  • `concept_art` for an ASSET-BUILD task (modeling/rigging/shading/etc.)
//    is filtered to ONLY the concept art submissions whose `parent_codes`
//    OR `parent_ids` contain the task's parent — so a Modeler for
//    `CH_the_witch` sees concept art for the witch only, NOT the entire
//    project's concept art library. Animatics/sequence-mode tasks still
//    see the full project's concept art (per founder spec, Feb 8 2026).
//  • All other approved deliverables — same project only.
const ASSET_BUILD_TASK_TYPES = new Set([
  'asset_build', 'modeling', 'texturing', 'shading', 'rigging', 'lookdev',
]);

export function rawMaterialsForTask(task, refs) {
  const buckets = bucketsForTask(task);
  if (!buckets.length || !refs) return [];
  const projectId = task?.project_id || '';
  const taskParentCode = task?.parent_code || '';
  const taskParentId = task?.parent_id || '';
  const isAssetBuildTask = ASSET_BUILD_TASK_TYPES.has(
    String(task?.task_type || '').toLowerCase().trim(),
  );
  const out = [];
  for (const b of buckets) {
    const raw = refs[b] || [];
    if (!Array.isArray(raw) || raw.length === 0) continue;
    let items = raw.filter(x => !projectId || x.project_id === projectId);
    // Asset-bound concept_art scoping — for modeling/rigging/etc., narrow
    // concept_art to only the asset's own concepts.
    if (b === 'concept_art' && isAssetBuildTask && (taskParentCode || taskParentId)) {
      items = items.filter(x => {
        const pcs = x.parent_codes || [];
        const pids = x.parent_ids || [];
        const codeMatch = taskParentCode && pcs.includes(taskParentCode);
        const idMatch = taskParentId && pids.includes(taskParentId);
        return codeMatch || idMatch;
      });
    }
    if (items.length) out.push({ bucket: b, items });
  }
  return out;
}

// Counts for the toggle pill ("Raw Materials (3 anim · 1 script · 5 link)")
export function rawMaterialCounts(task, refs) {
  const sections = rawMaterialsForTask(task, refs);
  const counts = {};
  let total = 0;
  for (const s of sections) {
    let n = s.items.length;
    if (s.bucket === 'reference_links') {
      // reference_links items are project-groups containing a `links` array
      n = s.items.reduce((sum, p) => sum + ((p.links || []).length), 0);
    }
    counts[s.bucket] = n;
    total += n;
  }
  return { counts, total, sections };
}

// Friendly label per bucket for the UI
export const BUCKET_LABEL = {
  scripts:         'Script & Brief',
  reference_links: 'Reference Links',
  concept_art:     'Concept Art',
  vo:              'Voice Overs (Approved)',
  animatics:       'Animatics (Approved)',
  asset_build:     'Asset Build (Approved)',
  rigging:         'Rigging (Approved)',
  shading:         'Shading (Approved)',
  layout:          'Layout (Approved)',
  animation:       'Animation (Approved)',
  lighting:        'Lighting (Approved)',
  fx_comp:         'FX / Comp (Approved)',
};

// Short label for the pill counter
export const BUCKET_SHORT = {
  scripts:         'script',
  reference_links: 'link',
  concept_art:     'concept',
  vo:              'VO',
  animatics:       'animatic',
  asset_build:     'asset',
  rigging:         'rig',
  shading:         'shade',
  layout:          'layout',
  animation:       'anim',
  lighting:        'light',
  fx_comp:         'fx',
};
